"use client"

import styled from "@emotion/styled"
import Container from "@eurodrive/ui/Container"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import { getCarTypeFromClassificationCode, usePathname } from "@eurodrive/utils"
import { ctaVehicleClicked } from "@eurodrive/utils/gtm"
import { Button, Typography } from "@mui/material"
import { LeasingCarDto } from "api"
import Link from "next/link"
import GridSwiper from "../GridSwiper"

const StyledContainer = styled.div`
  padding-top: 56px;
  padding-bottom: 64px;
  display: flex;
  flex-direction: column-reverse;
  gap: 48px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: grid;
    padding-top: 64px;
    grid-template-columns: 1fr 515px;
    gap: 108px;
  }
`

const ContentBox = styled.div`
  display: flex;
  padding: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 76px;
  border-radius: 8px;
  background: ${sharedColors.lightGrey};
`

const TotalSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
`

const DetailsGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 8px;
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  gap: 144px;
`

const Divider = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 10px;
  padding: 12px 0px;
`

const DividerLine = styled.div`
  height: 1px;
  background: #ece9e9;
  align-self: stretch;
`

const MonthlyPaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  padding: 12px 0px 0px;
`

const PriceContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
`

const StrikethroughContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 4px;
  height: 21px;
`

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap: 16px;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: 10px;
`

const ActionsInner = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 16px;
`

// Helper function to format price
const formatPrice = (price: number): string => {
  return `${price.toLocaleString("he-IL")} ₪`
}

export default function SpinDetails({
  data,
  desktopImages,
  mobileImages,
}: {
  data: LeasingCarDto
  desktopImages?: any
  mobileImages?: any
}) {
  const hasImages =
    (desktopImages && desktopImages.length > 0) ||
    (mobileImages && mobileImages.length > 0)
  // Extract data with fallbacks
  const pathname = usePathname()
  const dealDuration = data.dealMonthsNumber || 36
  const advancePrice = data.advancePrice || 0
  const monthlyPayment = data.monthlyPaymentPriceIncludingVat || 0
  const monthlyPaymentSale = data.monthlyPaymentSalePriceIncludingVat || 0
  const importerPrice = data.newCarParallelImporterPriceList || 0
  const manufacturerName = data.masterManufacturerDescription || ""

  // Use sale price if available, otherwise use regular monthly payment
  const displayPrice = monthlyPayment

  // Generate vehicleId for tracking
  const vehicleId = `leasing-${data.manufacturerCode}-${data.modelCode}`

  // Map classification code to carType for search URL
  const classificationCode =
    data.vehicleSingleValuedClassification?.[0]
      ?.vehicleSingleValuedClassificationCode
  const carType = getCarTypeFromClassificationCode(classificationCode)

  return (
    <Container paddingMobile>
      <StyledContainer>
        <ContentBox>
          <TotalSection>
            <DetailsGrid>
              <DetailRow>
                <Typography variant="body3">תקופת העסקה</Typography>
                <Typography variant="body2">{dealDuration} חודשים</Typography>
              </DetailRow>

              <DetailRow>
                <Typography variant="body3">20,000 ק״מ / שנה</Typography>
                <Typography variant="body2">כלול</Typography>
              </DetailRow>
            </DetailsGrid>

            <Divider>
              <DividerLine />
            </Divider>

            <DetailRow>
              <Typography variant="body3">מקדמה</Typography>
              <Typography variant="body2">
                {formatPrice(advancePrice)}
              </Typography>
            </DetailRow>

            <MonthlyPaymentRow>
              <Typography variant="body2">תשלום חודשי</Typography>
              <PriceContainer>
                {monthlyPaymentSale && (
                  <StrikethroughContainer>
                    <Typography
                      variant="body3"
                      sx={{ textDecoration: "line-through" }}
                    >
                      {formatPrice(monthlyPaymentSale)}
                    </Typography>
                  </StrikethroughContainer>
                )}
                <Typography variant="h3">
                  {formatPrice(displayPrice)}
                </Typography>
              </PriceContainer>
            </MonthlyPaymentRow>

            <Divider>
              <DividerLine />
            </Divider>

            <DetailRow>
              <Typography variant="body3">מחיר מחירון</Typography>
              <Typography variant="body2">
                {formatPrice(importerPrice)}
              </Typography>
            </DetailRow>
          </TotalSection>

          <ActionsSection>
            <ActionsContainer>
              <ActionsInner>
                <Button
                  variant="primary"
                  size="large"
                  LinkComponent={Link}
                  href={`${pathname}/configuration`}
                  fullWidth
                  onClick={() =>
                    ctaVehicleClicked(vehicleId, "leasing_car_page")
                  }
                >
                  להתאמת הרכב שלי
                </Button>
              </ActionsInner>

              <Button
                variant="secondary"
                size="large"
                LinkComponent={Link}
                href={`/search?dealType=leasing&carType=${carType}&manufacturer=${data.masterManufacturerCode || data.manufacturerCode}`}
                fullWidth
                onClick={() => ctaVehicleClicked(vehicleId, "leasing_car_page")}
              >
                לכל דגמי {manufacturerName.toUpperCase()}
              </Button>
            </ActionsContainer>
          </ActionsSection>
        </ContentBox>
        {hasImages && (
          <GridSwiper
            desktopImages={desktopImages}
            mobileImages={mobileImages}
            vehicleId={vehicleId}
          />
        )}
      </StyledContainer>
    </Container>
  )
}
